import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core'
import { FieldType } from '@ngx-formly/core'
import { Subject } from 'rxjs'

@Component({
  selector: 'app-formly-file-upload-box',
  templateUrl: './formly-file-upload-box.component.html',
  styleUrls: ['./formly-file-upload-box.component.scss'],
})
export class FormlyFileUploadBoxComponent
  extends FieldType
  implements OnInit, OnDestroy
{
  constructor(private cdr: ChangeDetectorRef) {
    super()
  }

  mimeTypeMap = {
    '.png': 'image/png',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.svg': 'image/svg+xml',
    '.xlsx':
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    '.csv': 'text/csv',
  }

  uploadedFiles: any[] = []
  defaultPlaceholder: string = 'Drag and drop your file here or '

  defaultAcceptedFileTypesDisplay: string = 'JPEG, JPG, PNG, SVG'
  defaultAcceptedFileTypes: string = '.JPEG, .JPG, .PNG, .SVG'
  showAcceptedFileTypes: boolean = false
  canRemove: boolean = false

  showTitle: boolean = false
  defaultTitle: string = 'File Upload'

  showSubtitle: boolean = false
  defaultSubtitle: string = 'Upload Image'

  showUploadMessageDetails: boolean = false
  defaultUploadMessageDetails: string =
    'Recommended Image Dimension: 48 x 48 px'

  defaultMultiple: boolean = false

  showProfileImageExample: boolean = false
  showBackgroundImageExample: boolean = false
  showOrganizationLogoExample: boolean = false
  showApplicationLogoExample: boolean = false
  clearFiles: Subject<void>
  dragover: boolean = false
  uploadedFilesText: string = ''

  ngOnInit(): void {
    this.clearFiles = this.field.props.clearFiles ?? new Subject<void>()
    this.clearFiles?.subscribe(() => {
      this.clearUploadedFiles()
    })
  }

  ngAfterViewInit(): void {
    document.querySelector('input').addEventListener('cancel', (evt) => {
      this.markAsTouched()
    })
  }

  ngOnDestroy(): void {
    this.clearFiles?.unsubscribe()
  }

  checkIfAcceptedType(uploadedFiles) {
    let acceptedTypes = (
      this.props.acceptedFileTypes
        ? this.props.acceptedFileTypes.toLowerCase()
        : this.defaultAcceptedFileTypes.toLowerCase()
    ).split(', ')
    let processedAcceptedTypes = []
    for (let type of acceptedTypes) {
      if (type in this.mimeTypeMap) {
        // if type is in our map, convert it to the corresponding MIME type
        processedAcceptedTypes.push(this.mimeTypeMap[type])
      } else if (type.includes('/*')) {
        // if type is a wildcard like "image/*", take the "image" part and use it to check file types
        processedAcceptedTypes.push(type.split('/')[0])
      } else {
        processedAcceptedTypes.push(type)
      }
    }

    for (let file of uploadedFiles) {
      const fileTypeMainPart = file.type.split('/')[0]
      const fileNameTypeArray = file.name.split('.')
      const fileNameType =
        fileNameTypeArray[fileNameTypeArray.length - 1].toLowerCase()
      if (
        !processedAcceptedTypes.includes(file.type) &&
        !processedAcceptedTypes.includes(fileTypeMainPart) &&
        !(
          file.type == '' &&
          processedAcceptedTypes
            .map((i) => i.toLowerCase())
            .includes(`.${fileNameType}`)
        )
      ) {
        console.error('File type not accepted:', file.type)
        return false // return false if a file has incorrect type
      }
    }

    return true
  }

  uploadFile(event: any) {
    const uploadedFiles = event.target.files ?? event.dataTransfer.files
    let files: any[] = []
    if (this.checkIfAcceptedType(uploadedFiles)) {
      for (const file of uploadedFiles) {
        this.uploadedFiles.push(file)
      }
      if (!this.props.multiple && this.uploadedFiles.length > 1) {
        this.uploadedFiles = [...this.uploadedFiles.slice(1)]
      }

      files = Object.assign([], this.uploadedFiles)
      this.field.model[this.field.key as string] = files
      this.form.controls[this.field.key as string].setErrors(null)
    }

    if (this.field.props.onFileSelect) {
      this.field.props.onFileSelect(files)
    }
    // Go ahead and clear the file input control.
    // This will allow the same files to be selected again.
    this.resetFileUploadControl(event.target)
  }

  private clearUploadedFiles() {
    this.uploadedFiles = []
    this.uploadedFilesText = ''

    this.field.model[this.field.key as string] = []
    this.form.controls[this.field.key as string].reset()
    this.form.controls[this.field.key as string].updateValueAndValidity()

    this.cdr.detectChanges()
  }

  private resetFileUploadControl(target: HTMLInputElement) {
    target.value = ''
  }

  dropHandler(event: any): void {
    event.preventDefault()
    this.dragover = false
    this.uploadFile(event)
  }

  dragOverHandler(event: any): void {
    this.dragover = true
    event.preventDefault()
  }

  deleteFile(index: number): void {
    this.uploadedFiles.splice(index, 1)
    this.model.files?.splice(index, 1)
    this.field.model[this.field.key as string].splice(index, 1)
    if (this.model.files?.length == 0) {
      this.field.formControl.reset()
    }
    this.field.formControl.markAsTouched()
    this.field.formControl.updateValueAndValidity()
    this.field.formControl.setErrors({ incorrect: true })
  }

  dragLeaveHandler(event: any): void {
    this.dragover = false
    event.preventDefault()
  }

  markAsTouched() {
    this.field.formControl.markAsTouched()
  }

  openImageInNew(): void {
    window.open(
      'https://digital-common-dev.azurewebsites.net/story-book/custom_bg_example_large.png'
    )
  }
}
